import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Icon, IconButton, Text, VStack} from '@chakra-ui/react';
import {useApi} from "@/uses/useApi";
import useScreenState from "@/uses/useScreenState";
import {
    GuideBody,
    GuideBodySkeleton,
    CreateOrEditGuide,
    EntityListBody,
    EntityListBodySkeleton,
    EntityListHeader,
    EntityListHeaderSkeleton,
    EntityModalContent,
    ListHeader,
    Paginator,
    PaginatorSkeleton, Button
} from "@/components";
import {useModal} from "@/contexts/modalContext";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";
import {Plus} from 'lucide-react';

const PER_PAGE = 20;

export const GuideList = () => {
    const navigate = useNavigate();
    const {language, availableLanguages, t} = useLanguage();
    const sizes = {base: '95%', md: '95%', lg: '90%'};
    const api = useApi();
    const [entities, setEntities] = useState([]);

    const possibleSorts = [
        ['sortOrder', 'asc', t('sentences.sortByField', { field: t('fields.order'), order: t('orders.asc') })],
        ['sortOrder', 'desc', t('sentences.sortByField', { field: t('fields.order'), order: t('orders.desc') })],
        ['createdAt', 'desc', t('sentences.sortByField', { field: t('fields.createdAt'), order: t('orders.desc') })],
        ['createdAt', 'asc', t('sentences.sortByField', { field: t('fields.createdAt'), order: t('orders.asc') })],
        [`${language}Title`, 'asc', t('sentences.sortByField', { field: t('fields.title'), order: t('orders.asc') })],
        [`${language}Title`, 'desc', t('sentences.sortByField', { field: t('fields.title'), order: t('orders.desc') })],
    ]

    const {open, close} = useModal();
    const [itemNumber, setItemNumber] = useState(null);
    const [currentQueryParameters, setSearchParams] = useSearchParams();
    const getDefaultState = useCallback((pageParams) => {
        const result = {}

        /** Retrieve filters param */
        const filter = pageParams.get('filter');
        try {
            result.filter = JSON.parse(filter);
            if (objectUtil.isObject(result.filter)) {
                result.filter = Object.fromEntries(Object.entries(result.filter).filter(([_, value]) => (value ?? []).length > 0));
            }
        } catch (e) {
            console.error(e);
            result.filter = [];
        }
        /** Retrieve search param */
        result.search = pageParams.get('search') ?? '';

        /** Retrieve sort param */
        const sort = (pageParams.get('sort') ?? '').split('-');
        result.sort = possibleSorts.find(([col, type]) => sort[0] === col && sort[1] === type) ?? possibleSorts?.[0];

        /** Retrieve page param */
        const parsedPage = parseInt(pageParams.get('page'), 10);
        if (isNaN(parsedPage) || parsedPage <= 0) {
            result.page = 0;
        } else {
            result.page = parsedPage - 1;
        }
        return result;
    }, []);
    const defaultParams = getDefaultState(currentQueryParameters);
    const [filters, setFilters] = useState({});
    const [getParams, setGetParams] = useState({
        sort: defaultParams.sort,
        ...(defaultParams.page > 0 ? {page: defaultParams.page} : {}),
        ...(defaultParams.search && defaultParams.search.length >= process.env.REACT_APP_MIN_SEARCH_LENGTH ? {search: defaultParams.search} : {}),
        ...(Object.keys(defaultParams.filter ?? {}).length > 0 ? {filter: defaultParams.filter} : {}),
    });

    const {state, realState, isMounted, setState} = useScreenState();


    const reqParams = useRef(null);
    const guideTypes = useRef(null);
    const getPage = useCallback(() => {
        const newState = reqParams.current !== null &&
            objectUtil
                .difference(getParams, reqParams.current ?? {})
                .filter((e) => !['page','sort'].includes(e))
                .length !== 0
            ? 1 : 0;
        setState((prevState) => prevState === -1 ? prevState : newState);
        reqParams.current = getParams
        /** Les types servent au filtre et à l'affichage du type sur chaque ligne */
        const typesPromise = guideTypes.current !== null
            ? Promise.resolve(guideTypes.current)
            : api.ApiService.get('guide_type', {getAll: true})
                .then((res) => res?.data?.type === 'success' ? (res.data.value ?? []) : [])
                .catch(() => [])
                .then((types) => (guideTypes.current = types));
        return Promise.all([typesPromise, api.ApiService.get('guide', {
            ...reqParams.current,
            limit: PER_PAGE,
            locale: language,
            ...(state === -1 ? {needFilters: 'bool:true'} : {})
        })])
            .then(([types, res]) => {
                if (res?.data?.type === 'success') {
                    const typeTitle = (id) => {
                        const type = types.find((e) => e.id === id);
                        return type?.[`${language}Title`] ?? type?.frTitle ?? null;
                    };
                    setItemNumber(() => res?.data?.value?.estimatedTotalHits);
                    if (res?.data?.value?.facetDistribution) {
                        setFilters(() => ({
                            type: {
                                placeholder: t('sentences.actionField', {action: t('actions.filterBy'), element: t('guides.guideType')}),
                                items: Object.entries(res.data.value.facetDistribution?.type ?? {}).map(([id, records]) => ({
                                    label: `${typeTitle(parseInt(id, 10)) ?? id} (${records?.records ?? records})`,
                                    value: parseInt(id, 10),
                                })),
                            },
                        }));
                    }
                    setEntities(() => (res?.data?.value?.hits ?? []).map((entity) => {
                        return {
                            ...entity,
                            typeTitle: typeTitle(entity.type),
                        }
                    }));
                }
            })
            .catch(e => console.log(e?.response?.data))
            .finally(() => setState(_ => 2))
    }, [getParams, state]);

    useEffect(() => {
        getPage().then(() => {
            setSearchParams(prev => {
                if ('search' in getParams) {
                    prev.set('search', getParams.search);
                } else {
                    prev.delete('search');
                }

                if ('filter' in getParams) {
                    prev.set('filter', JSON.stringify(getParams.filter));
                } else {
                    prev.delete('filter');
                }

                if ('sort' in getParams) {
                    prev.set('sort', getParams.sort.slice(0, 2).join('-'));
                } else {
                    prev.delete('sort');
                }

                if ('page' in getParams) {
                    prev.set('page', (getParams.page + 1).toString());
                } else {
                    prev.delete('page');
                }

                return prev;
            })
        })
    }, [getParams]);


    const hasData = useMemo(() => {
        return isMounted && (entities.length > 0 || Object.keys(objectUtil.filterByKeys(getParams, ['sort'], false)).length > 0)
    }, [isMounted, entities, getParams]);

    const noDataText = t('sentences.noEntityYet.guide');

    const Table = useMemo(() => {
        return hasData
            ?
            <VStack position="relative" w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeader
                    after={<IconButton
                        bg={"secondary.500"}
                        onClick={(_) => {
                            open(<EntityModalContent
                                title={t('add')}
                                    Body={<CreateOrEditGuide
                                    successCallback={() => {
                                        navigate(0);
                                    }}
                                />}
                                close={close}
                            />, 'cover')
                        }}
                    >
                        <Icon as={Plus}/>
                    </IconButton>}
                    setGetParams={setGetParams}
                    filters={filters}
                    defaultParams={defaultParams}
                    sizes={sizes}
                    possibleSorts={possibleSorts}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    {
                        [0, 1].includes(realState)
                            ? <EntityListBodySkeleton
                                Body={GuideBodySkeleton}
                                sizes={sizes}/>
                            : <EntityListBody
                                noDataText={noDataText}
                                entity="guide"
                                Body={GuideBody}
                                EditBody={CreateOrEditGuide}
                                sizes={sizes}
                                entities={entities}
                                setEntities={setEntities}
                                getDeletionText={(e) => `"${e?.[`${language}Title`] ?? ''}"`.slice(0,200)}
                            />
                    }
                </VStack>
                {
                    realState === 1
                        ? <PaginatorSkeleton/>
                        : <Paginator
                            defaultPage={defaultParams.page}
                            setGetParams={setGetParams}
                            itemNumber={itemNumber}
                            perPage={PER_PAGE}
                        />
                }
            </VStack>
            :
            <VStack align="center" justify="center">
                <Text>{noDataText}</Text>
                <IconButton
                    bg={'secondary.500'}
                    onClick={(_) => {
                        open(<EntityModalContent
                            title={t('actions.add')}
                            Body={<CreateOrEditGuide
                                successCallback={() => {
                                    navigate(0);
                                }}
                            />}
                            close={close}
                        />, 'cover')
                    }}
                >
                    <Icon as={Plus}/>
                </IconButton>
            </VStack>
    }, [hasData, realState, entities, setEntities, setGetParams, filters, defaultParams, sizes, possibleSorts, itemNumber, close, navigate])

    const Content = useMemo(() => {
        return isMounted
            ? Table
            : <VStack w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeaderSkeleton
                    sizes={sizes}
                    after={<IconButton/>}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    <EntityListBodySkeleton
                        Body={GuideBodySkeleton}
                        sizes={sizes}/>
                </VStack>
                <PaginatorSkeleton/>
            </VStack>
    }, [isMounted, Table])

    return (
        <VStack w="100%" overflow="hidden">
            <ListHeader title={t('sentences.managementOfEntities', { entity: t('objects.guide_other') })} description={t('sentences.manageEntities', { action1: t('actions.add'), action2: t('actions.edit'), action3: t('actions.view'), entity: t('objects.guide_other')})}/>
            {
                Content
            }
        </VStack>
    );
};

export default GuideList;
